import { ReactElement, ReactNode, useState } from 'react'
import {
  BottomSheet,
  Checkbox,
  DateTimeInput,
  MultiSelectField,
  salesReportIcons,
  SegmentedControl,
  SelectField,
} from '../../../components/SalesReport'
import { SalesReportFilterOptions } from '../../../hooks/sales-report'
import { useMediaQuery } from '../../../hooks/useMediaQuery'
import {
  activeFilterLabels,
  COURTESY_OPTIONS,
  CourtesyMode,
  DEFAULT_FILTERS,
  GROUP_BY_OPTIONS,
  groupByLabel,
  groupsByCommissioner,
  groupsByPayment,
  lotOptions,
  MAX_SELECTED_IDS,
  pdvOptions,
  periodError,
  SalesReportFilterState,
  SalesReportGroupBy,
  sectorOptions,
  ticketOptions,
  withSectors,
} from '../filters'
import * as S from './styles'

interface FiltersPanelProps {
  options: SalesReportFilterOptions
  value: SalesReportFilterState
  onChange: (next: SalesReportFilterState) => void
  canSeeStock: boolean
  canSeeFee: boolean
  onPeriodCommit?: (periodStart: string, periodEnd: string) => void
}

interface FieldsInput {
  options: SalesReportFilterOptions
  state: SalesReportFilterState
  change: (next: SalesReportFilterState) => void
  canSeeStock: boolean
  canSeeFee: boolean
  mobile: boolean
  onPeriodCommit?: () => void
}

interface Fields {
  start: ReactNode
  end: ReactNode
  error: string | null
  sector: ReactNode
  ticket: ReactNode
  pdv: ReactNode
  lot: ReactNode
  groupBy: ReactNode
  courtesy: ReactNode
  checkboxes: ReactNode[]
}

const PERIOD_ERROR_ID = 'sales-report-period-error'

const buildFields = ({ options, state, change, canSeeStock, canSeeFee, mobile, onPeriodCommit }: FieldsInput): Fields => {
  const set = <K extends keyof SalesReportFilterState>(key: K, next: SalesReportFilterState[K]): void =>
    change({ ...state, [key]: next })
  const error = periodError(state)
  const multiSelect = { maxSelected: MAX_SELECTED_IDS, dense: mobile, autoFocusSearch: !mobile, inline: mobile }

  return {
    error,
    start: (
      <DateTimeInput
        id="sales-report-period-start"
        label="Período — Início"
        value={state.periodStart}
        onChange={(next) => set('periodStart', next)}
        onCommit={onPeriodCommit}
        dense={mobile}
        invalid={error != null}
        describedBy={error != null ? PERIOD_ERROR_ID : undefined}
      />
    ),
    end: (
      <DateTimeInput
        id="sales-report-period-end"
        label="Período — Fim"
        value={state.periodEnd}
        onChange={(next) => set('periodEnd', next)}
        onCommit={onPeriodCommit}
        dense={mobile}
        invalid={error != null}
        describedBy={error != null ? PERIOD_ERROR_ID : undefined}
      />
    ),
    sector: (
      <MultiSelectField
        id="sales-report-sector"
        label="Setor"
        value={state.sectorIds}
        options={sectorOptions(options)}
        onChange={(next) => change(withSectors(state, next, options))}
        {...multiSelect}
      />
    ),
    ticket: (
      <MultiSelectField
        id="sales-report-ticket"
        label="Ingresso"
        value={state.ticketIds}
        options={ticketOptions(options, state.sectorIds)}
        onChange={(next) => set('ticketIds', next)}
        align={mobile ? 'start' : 'end'}
        {...multiSelect}
      />
    ),
    pdv: (
      <MultiSelectField
        id="sales-report-pdv"
        label="Ponto de Venda"
        value={state.pdvIds}
        options={pdvOptions(options)}
        onChange={(next) => set('pdvIds', next)}
        {...multiSelect}
      />
    ),
    lot: (
      <MultiSelectField
        id="sales-report-lot"
        label="Lote"
        value={state.lotIds}
        options={lotOptions(options)}
        onChange={(next) => set('lotIds', next)}
        {...multiSelect}
      />
    ),
    groupBy: (
      <SelectField
        id="sales-report-group-by"
        label="Agrupar por"
        value={state.groupBy}
        options={GROUP_BY_OPTIONS}
        onChange={(next) => set('groupBy', next as SalesReportGroupBy)}
        inline={!mobile}
        dense={mobile}
        width={mobile ? undefined : '192px'}
      />
    ),
    courtesy: (
      <SegmentedControl
        ariaLabel="Cortesias"
        options={COURTESY_OPTIONS}
        value={state.courtesy}
        onChange={(next: CourtesyMode) => set('courtesy', next)}
      />
    ),
    checkboxes: [
      canSeeStock && (
        <Checkbox
          key="stock"
          label="Exibir estoque"
          checked={state.showStock}
          onChange={(next) => set('showStock', next)}
          dense={mobile}
        />
      ),
      <Checkbox
        key="commissioners"
        label="Incluir comissários"
        checked={state.includeCommissioners || groupsByCommissioner(state.groupBy)}
        onChange={(next) => set('includeCommissioners', next)}
        disabled={groupsByCommissioner(state.groupBy)}
        dense={mobile}
      />,
      canSeeFee && (
        <Checkbox
          key="fee"
          label="Exibir taxa adicional"
          checked={state.showFee}
          onChange={(next) => set('showFee', next)}
          dense={mobile}
        />
      ),
      <Checkbox
        key="payment"
        label="Exibir forma de pagamento"
        checked={groupsByPayment(state.groupBy) ? state.groupBy === 'commissionerPayment' : state.showPaymentMethod}
        onChange={(next) => set('showPaymentMethod', next)}
        disabled={groupsByPayment(state.groupBy)}
        dense={mobile}
      />,
    ].filter(Boolean),
  }
}

function MobileFilters({ options, value, onChange, canSeeStock, canSeeFee, onPeriodCommit }: FiltersPanelProps): ReactElement {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<SalesReportFilterState>(value)
  const labels = activeFilterLabels(value, options)
  const fields = buildFields({ options, state: draft, change: setDraft, canSeeStock, canSeeFee, mobile: true })

  const openSheet = (): void => {
    setDraft(value)
    setOpen(true)
  }
  const apply = (): void => {
    onChange(draft)
    onPeriodCommit?.(draft.periodStart, draft.periodEnd)
    setOpen(false)
  }

  return (
    <>
      <S.MobileBar>
        <S.FiltersButton
          type="button"
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={labels.length > 0 ? `Filtros, ${labels.length} ativos` : 'Filtros'}
          onClick={openSheet}
        >
          <img src={salesReportIcons.sliders} alt="" width={16} height={16} />
          Filtros
          {labels.length > 0 && <S.Counter aria-hidden="true">{labels.length}</S.Counter>}
        </S.FiltersButton>
        <S.MobileSummary>
          <S.SummaryText>{labels.length > 0 ? labels.join(' · ') : 'Sem filtros aplicados'}</S.SummaryText>
          <S.SummaryText $muted>{`Agrupado por ${groupByLabel(value.groupBy)}`}</S.SummaryText>
        </S.MobileSummary>
      </S.MobileBar>
      <BottomSheet
        open={open}
        title="Filtros"
        onClose={() => setOpen(false)}
        footer={
          <>
            <S.SheetButton type="button" onClick={() => setDraft(DEFAULT_FILTERS)}>
              Limpar
            </S.SheetButton>
            <S.SheetButton type="button" $primary disabled={fields.error != null} onClick={apply}>
              Ver resultados
            </S.SheetButton>
          </>
        }
      >
        <S.SheetContent>
          <S.MobileStack $gap="12px">
            {fields.start}
            {fields.end}
            {fields.error && (
              <S.MobilePeriodError id={PERIOD_ERROR_ID} role="alert">
                {fields.error}
              </S.MobilePeriodError>
            )}
          </S.MobileStack>
          <S.MobileStack $gap="12px">
            {fields.sector}
            {fields.ticket}
            {fields.pdv}
            {fields.lot}
          </S.MobileStack>
          <S.SheetSection aria-labelledby="sales-report-display-title">
            <S.MobileTitle id="sales-report-display-title">Agrupamento e exibição</S.MobileTitle>
            {fields.groupBy}
            <S.MobileStack $gap="8px">
              <S.MobileLabel>Cortesias</S.MobileLabel>
              {fields.courtesy}
            </S.MobileStack>
            <S.MobileStack $gap="12px">{fields.checkboxes}</S.MobileStack>
          </S.SheetSection>
        </S.SheetContent>
      </BottomSheet>
    </>
  )
}

export function FiltersPanel(props: FiltersPanelProps): ReactElement {
  const mobile = useMediaQuery('(max-width: 767px)')
  if (mobile) return <MobileFilters {...props} />

  const { options, value, onChange, canSeeStock, canSeeFee, onPeriodCommit } = props
  const fields = buildFields({
    options,
    state: value,
    change: onChange,
    canSeeStock,
    canSeeFee,
    mobile: false,
    onPeriodCommit: () => onPeriodCommit?.(value.periodStart, value.periodEnd),
  })

  return (
    <S.Card aria-label="Filtros">
      <S.FieldsGrid $padding="20px 24px 16px">
        {fields.start}
        {fields.end}
        {fields.sector}
        {fields.ticket}
      </S.FieldsGrid>
      {fields.error && (
        <S.PeriodError id={PERIOD_ERROR_ID} role="alert">
          {fields.error}
        </S.PeriodError>
      )}
      <S.FieldsGrid $padding="0 24px 20px">
        <S.Span2>{fields.pdv}</S.Span2>
        <S.Span2>{fields.lot}</S.Span2>
      </S.FieldsGrid>
      <S.Strip>
        <S.StripRow>
          {fields.groupBy}
          <S.VerticalDivider aria-hidden="true" />
          <S.CourtesyGroup>
            <S.InlineLabel>Cortesias</S.InlineLabel>
            {fields.courtesy}
          </S.CourtesyGroup>
        </S.StripRow>
        <S.HorizontalDivider aria-hidden="true" />
        <S.StripRow>
          {fields.checkboxes.map((checkbox, index) => (
            <S.CheckboxCell key={index}>{checkbox}</S.CheckboxCell>
          ))}
        </S.StripRow>
      </S.Strip>
    </S.Card>
  )
}
